import { useEffect, useRef, useState } from 'react'
import { gsap, EASE_OUT, prefersReducedMotion } from './gsap'
import { StateTag } from './StateTag'
import { Etiqueta, Dado } from './type'

export interface ProofStat { label: string; to: number; decimals?: number; suffix?: string }
export interface ProofCardCopy { when: string; title: string; body: string; timingLabel: string; timingUnit: string; timingTo: number }

/**
 * Cartão de prova (portado do modelo do Ângelo): os números da execução real do Genesis contam de 0 ao valor quando o cartão
 * entra em vista, e a etiqueta de estado passa de "Em correção" (Construção) a "Fechado" (Cura) — cor semântica usada como
 * mudança de estado real, não decoração. Reduced-motion: valores finais desde o primeiro frame. Chapa `fio` com borda de linha.
 * A etiqueta é UMA (estado React), nunca duas com display:none — elemento oculto some do fluxo e engana o gate de margem.
 */
export function ProofCard({ copy, stats, className }: { copy: ProofCardCopy; stats: ProofStat[]; className?: string }) {
  const card = useRef<HTMLDivElement>(null)
  const [closed, setClosed] = useState(false)
  const fmt = (s: ProofStat, v: number) => `${v.toFixed(s.decimals ?? 0)}${s.suffix ?? ''}`
  useEffect(() => {
    const el = card.current
    if (!el) return
    const values = Array.from(el.querySelectorAll<HTMLElement>('[data-stat]'))
    const timing = el.querySelector<HTMLElement>('[data-timing]')
    const setFinal = () => { values.forEach((v, i) => { v.textContent = fmt(stats[i], stats[i].to) }); if (timing) timing.textContent = `≈ ${copy.timingTo} ${copy.timingUnit}`; setClosed(true) }
    if (prefersReducedMotion()) { setFinal(); return }
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ scrollTrigger: { trigger: el, start: 'top 85%', once: true } })
      stats.forEach((s, i) => {
        const v = values[i]; if (!v) return
        if (s.to === 0) { tl.from(v, { opacity: 0, duration: 0.3 }, i * 0.12); return }
        const proxy = { v: 0 }
        tl.to(proxy, { v: s.to, duration: 0.9, ease: EASE_OUT, onUpdate: () => { v.textContent = fmt(s, proxy.v) } }, i * 0.12)
      })
      if (timing) { const p = { v: 0 }; tl.to(p, { v: copy.timingTo, duration: 0.9, ease: EASE_OUT, onUpdate: () => { timing.textContent = `≈ ${Math.round(p.v)} ${copy.timingUnit}` } }, 0.1) }
      tl.call(() => setClosed(true), undefined, '+=0.3')
    }, el)
    return () => ctx.revert()
  }, [copy, stats])
  return (
    <div ref={card} className={['z-proofcard', className].filter(Boolean).join(' ')} data-anatomy="campo">
      <div className="z-proofcard__topo">
        <Dado as="span">{copy.when}</Dado>
        <StateTag state={closed ? 'cura' : 'construcao'} />
      </div>
      <p className="z-proofcard__titulo">{copy.title}</p>
      <p className="z-proofcard__corpo">{copy.body}</p>
      <dl className="z-proofcard__grade">
        {stats.map((s) => (
          <div key={s.label}><dt><Etiqueta as="span">{s.label}</Etiqueta></dt><dd data-stat className="z-proofcard__valor">{fmt(s, 0)}</dd></div>
        ))}
      </dl>
      <p className="z-proofcard__tempo"><Dado as="span">{copy.timingLabel} <span data-timing className="z-proofcard__cura">≈ 0 {copy.timingUnit}</span></Dado></p>
    </div>
  )
}
